import { isPlatformBrowser } from '@angular/common';
import { computed, DestroyRef, inject, Injectable, InjectionToken, Injector, PLATFORM_ID, signal } from '@angular/core';

import { environment } from '../../../environments/environment';
import { SupabaseService } from '../auth/supabase.service';
import { ActiveCallService } from '../telephony/active-call.service';

export interface AccessibleClinic {
  readonly id: number;
  readonly name: string;
}

/** A clinic the user can make their working clinic (public.my_switchable_clinics). */
export interface SwitchableClinic {
  readonly id: number;
  /** "Company · Clinic", or the company alone for its main clinic (crm.clinic_label). */
  readonly label: string;
  /** The clinic's own part of the label; the whole label when there is none. */
  readonly name: string;
  readonly company: string;
  readonly active: boolean;
}

/** What stops a switch: a call in progress would be cut by the reload. */
export type SwitchBlocker = 'call_active' | null;

/** Splits crm.clinic_label's "Company · Clinic" into its two parts. */
export function splitClinicLabel(label: string): { readonly company: string; readonly name: string } {
  const at = label.indexOf(' · ');
  return at < 0 ? { company: label, name: label } : { company: label.slice(0, at), name: label.slice(at + 3) };
}

/** Per tab: the clinic just switched to, read once after the reload to say so. */
export const SWITCHED_NOTICE_KEY = 'metasmile.clinic-switched';

/** Left by the client-side pin this service used to keep; removed on sight. */
const LEGACY_PIN_KEY = 'metasmile.activeClinicId';

const CHANNEL_NAME = 'metasmile.clinic-switch';

/** Leaves the page for a fresh load of the CRM. A token so specs can see it without reloading. */
export const CLINIC_SWITCH_RELOAD = new InjectionToken<(url: string) => void>('CLINIC_SWITCH_RELOAD', {
  providedIn: 'root',
  factory: () => (url: string) => window.location.assign(url),
});

/**
 * Which clinic the CRM is showing: always the user's working clinic on the server
 * (users_tb.branches_id), which crm.current_branch_id(), the RLS policies and the plan lock
 * all read. The services filter their queries by `activeClinicId()` as well.
 *
 * Company owners can switch clinic (supabase/migrations/20261028094800_switch_clinic.sql):
 * the switch moves the working clinic on the server, so every rule follows it, and the CRM
 * then loads again from the start. The clinic app and the CRM share it: a switch in one is
 * picked up by the other when its tab comes back into view. The admin portal has no switcher;
 * its pages take the clinic from the link.
 *
 * `accessibleClinics()` names every clinic the user can see rows of (a platform admin: all of
 * them), for labels and the attribution report's filter; it is not the switcher's list.
 */
@Injectable({ providedIn: 'root' })
export class ActiveClinicService {
  private readonly supabase = inject(SupabaseService);
  private readonly injector = inject(Injector);
  private readonly reload = inject(CLINIC_SWITCH_RELOAD);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);

  private readonly _accessibleClinics = signal<readonly AccessibleClinic[]>([]);
  readonly accessibleClinics = this._accessibleClinics.asReadonly();

  private readonly _activeClinicId = signal<number | null>(null);
  readonly activeClinicId = this._activeClinicId.asReadonly();

  private readonly _switchableClinics = signal<readonly SwitchableClinic[]>([]);
  readonly switchableClinics = this._switchableClinics.asReadonly();
  readonly activeSwitchable = computed(() => this._switchableClinics().find((c) => c.active) ?? null);
  /** Owners of a company with several clinics, in the CRM portal. */
  readonly canSwitch = computed(() => this._switchableClinics().length > 1);

  private readonly _switchingTo = signal<SwitchableClinic | null>(null);
  readonly switchingTo = this._switchingTo.asReadonly();

  private channel: BroadcastChannel | null = null;
  private listening = false;

  async refresh(homeClinicId: number | null): Promise<void> {
    this.forgetLegacyPin();
    const client = this.supabase.client;
    const [accessible, switchable] = await Promise.all([
      client.schema('crm').rpc('my_accessible_clinics'),
      environment.platform === 'crm' ? client.rpc('my_switchable_clinics') : Promise.resolve({ data: [], error: null }),
    ]);

    const clinics: AccessibleClinic[] =
      accessible.error || !accessible.data
        ? []
        : (accessible.data as { branches_id: number; branch_name: string }[]).map((row) => ({
            id: row.branches_id,
            name: row.branch_name,
          }));
    this._accessibleClinics.set(clinics);
    this._activeClinicId.set(homeClinicId ?? clinics[0]?.id ?? null);

    this._switchableClinics.set(
      switchable.error || !Array.isArray(switchable.data)
        ? []
        : (switchable.data as { branches_id: number; label: string | null; is_active: boolean }[]).map((row) => ({
            id: Number(row.branches_id),
            label: row.label ?? '',
            ...splitClinicLabel(row.label ?? ''),
            active: row.is_active === true,
          })),
    );
  }

  clear(): void {
    this._accessibleClinics.set([]);
    this._activeClinicId.set(null);
    this._switchableClinics.set([]);
    this._switchingTo.set(null);
  }

  /** What would stop a switch now. Looked up here: the call service is not needed until then. */
  blocker(): SwitchBlocker {
    return this.injector.get(ActiveCallService).isActive() ? 'call_active' : null;
  }

  /**
   * Moves the user to another clinic and loads the CRM again there. Rejects, with the
   * switcher usable again, when the server refuses or cannot be reached.
   */
  async switchTo(clinic: SwitchableClinic): Promise<void> {
    if (clinic.active || this._switchingTo()) return;
    this._switchingTo.set(clinic);
    try {
      const { error } = await this.supabase.client.rpc('set_active_clinic', { p_branch_id: clinic.id });
      if (error) throw error;
    } catch (err) {
      this._switchingTo.set(null);
      throw err;
    }
    this.channel?.postMessage({ branchId: clinic.id, label: clinic.label });
    this.leave(clinic.label);
  }

  /**
   * Follows a switch made elsewhere: another CRM tab (at once), or the clinic app or another
   * device (when this tab comes back into view and the server's working clinic is no longer
   * the one on screen). Called once, from App.
   */
  listen(): void {
    if (!this.isBrowser || this.listening) return;
    this.listening = true;
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent<{ label?: string }>) => this.leave(event.data?.label ?? '');
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') void this.checkServerClinic();
    };
    document.addEventListener('visibilitychange', onVisible);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', onVisible);
      this.channel?.close();
    });
  }

  /** The label of the clinic switched to just before this load, once; null otherwise. */
  takeSwitchedNotice(): string | null {
    if (!this.isBrowser) return null;
    try {
      const label = sessionStorage.getItem(SWITCHED_NOTICE_KEY);
      sessionStorage.removeItem(SWITCHED_NOTICE_KEY);
      return label;
    } catch {
      return null;
    }
  }

  /** Only owners can be moved, so only they need the check. */
  private async checkServerClinic(): Promise<void> {
    if (!this.canSwitch() || this._switchingTo()) return;
    const { data, error } = await this.supabase.client.schema('crm').rpc('current_branch_id');
    const now = typeof data === 'number' ? data : Number(data);
    if (error || !now || now === this._activeClinicId()) return;
    const label = this._switchableClinics().find((c) => c.id === now)?.label ?? '';
    this.leave(label);
  }

  private leave(label: string): void {
    try {
      if (label) sessionStorage.setItem(SWITCHED_NOTICE_KEY, label);
    } catch {
      // Blocked storage: the switch still happens, only the notice is lost.
    }
    // The start, not the page on screen: a lead or conversation open here is the old clinic's.
    this.reload('/');
  }

  private forgetLegacyPin(): void {
    if (!this.isBrowser) return;
    try {
      localStorage.removeItem(LEGACY_PIN_KEY);
    } catch {
      // Private-browsing contexts can throw; nothing is pinned then anyway.
    }
  }
}
